(function () {
  const ELEMENT_NAME = 'fa-galerie-collection';

  class GalerieCollection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        if (this.config) {
          this.requestDeliveryEstimate();
          this.bindDeliveryDock();
        }
        return;
      }
      this.initialized = true;
      this.config = this.readConfig();
      if (!this.config) return;

      this.locale = String(this.config.locale || document.documentElement.lang || 'de');
      this.i18n = this.config.i18n || {};
      this.items = (Array.isArray(this.config.items) ? this.config.items : [])
        .map((item) => this.normalizeItem(item))
        .filter((item) => item.shortSide > 0 && item.longSide > 0 && item.variantId);
      this.items.sort((left, right) => left.shortSide - right.shortSide || left.longSide - right.longSide);
      this.byFormat = new Map(this.items.map((item) => [this.formatKey(item.shortSide, item.longSide), item]));
      this.dimensions = this.selectableDimensions();
      this.quantity = 1;
      this.width = 50;
      this.height = 70;
      this.supportOverrides = {};
      this.deliveryDockDismissed = false;
      this.deliveryText = '';
      this.deliveryError = false;

      this.bindControls();
      this.configureRanges();
      this.readInitialFormat();
      this.renderDirectory();
      this.updateSelection({ updateUrl: false });
      this.loadSupportMap();
      this.requestDeliveryEstimate();
      this.bindDeliveryDock();
    }

    disconnectedCallback() {
      const controller = this.deliveryController;
      this.deliveryController = null;
      if (controller) {
        this.deliveryRequested = false;
        controller.abort();
      }
      window.cancelAnimationFrame(this.deliveryDockFrame);
      if (!this.deliveryDockUpdate) return;
      window.removeEventListener('scroll', this.deliveryDockUpdate);
      window.removeEventListener('resize', this.deliveryDockUpdate);
    }

    readConfig() {
      try {
        return JSON.parse(this.querySelector('[data-fa-gallery-config]')?.textContent || '');
      } catch (error) {
        console.error('Galerie collection configuration could not be read.', error);
        return null;
      }
    }

    normalizeItem(item) {
      return {
        ...item,
        shortSide: this.readNumber(item.shortSide, 0),
        longSide: this.readNumber(item.longSide, 0),
        price: this.readNumber(item.price, 0),
        compareAtPrice: this.readNumber(item.compareAtPrice, 0),
        tierRules: this.normalizeTierRules(item.tierRules),
      };
    }

    normalizeTierRules(rules) {
      if (!Array.isArray(rules)) return [];
      const currency = String(this.config?.currency || 'CHF').toUpperCase();
      return rules
        .filter((rule) => rule && rule.active !== false && String(rule.pricingType || 'FIXED_PRICE') === 'FIXED_PRICE')
        .map((rule) => ({
          priority: this.readNumber(rule.priority, 0),
          tiers: (Array.isArray(rule.tiers) ? rule.tiers : [])
            .map((tier) => ({
              minQuantity: this.readNumber(tier.minQuantity, 0),
              price: this.readNumber(tier.pricingValue ?? tier.fixedVpePriceCents, 0),
              currency: String(tier.currency || currency).toUpperCase(),
            }))
            .filter((tier) => tier.minQuantity > 0 && tier.price > 0 && tier.currency === currency)
            .sort((left, right) => left.minQuantity - right.minQuantity),
        }))
        .filter((rule) => rule.tiers.length)
        .sort((left, right) => right.priority - left.priority);
    }

    bindControls() {
      this.querySelectorAll('[data-number]').forEach((input) => {
        input.addEventListener('change', () => {
          const dimension = input.dataset.number;
          this[dimension] = this.readNumber(input.value, this[dimension]);
          this.syncRange(dimension);
          this.updateSelection();
        });
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            input.blur();
          }
        });
      });

      this.querySelectorAll('[data-range]').forEach((range) => {
        range.addEventListener('input', () => {
          const dimension = range.dataset.range;
          const value = this.dimensions[this.readNumber(range.value, 0)];
          if (!value) return;
          this[dimension] = value;
          this.querySelector(`[data-number="${dimension}"]`).value = value;
          this.updateRangeVisual(dimension);
          this.updateSelection();
        });
      });

      this.querySelectorAll('[data-dimension] [data-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const dimension = button.closest('[data-dimension]').dataset.dimension;
          const direction = this.readNumber(button.dataset.step, 0);
          const currentIndex = this.nearestDimensionIndex(this[dimension]);
          const nextIndex = this.clamp(currentIndex + direction, 0, this.dimensions.length - 1);
          this[dimension] = this.dimensions[nextIndex];
          this.querySelector(`[data-number="${dimension}"]`).value = this[dimension];
          this.syncRange(dimension);
          this.updateSelection();
        });
      });

      this.querySelector('[data-direct-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        const input = this.querySelector('[data-direct-input]');
        const parsed = this.parseFormat(input?.value || '');
        if (!parsed) {
          input?.setAttribute('aria-invalid', 'true');
          this.renderInvalid();
          return;
        }
        input?.removeAttribute('aria-invalid');
        this.width = parsed[0];
        this.height = parsed[1];
        this.syncInputs();
        this.updateSelection();
      });

      this.querySelector('[data-result]')?.addEventListener('click', (event) => {
        if (event.target.closest('[data-selected-delivery-retry]')) {
          this.requestDeliveryEstimate(true);
          return;
        }
        const tierButton = event.target.closest('[data-tier-quantity]');
        if (tierButton) {
          this.quantity = this.clamp(this.readNumber(tierButton.dataset.tierQuantity, 1), 1, 999);
          const input = this.querySelector('[data-selected-quantity]');
          if (input) input.value = this.quantity;
          this.updateSelectedPrices();
          return;
        }
        const stepButton = event.target.closest('[data-selected-step]');
        if (stepButton) {
          this.quantity = this.clamp(this.quantity + this.readNumber(stepButton.dataset.selectedStep, 0), 1, 999);
          const input = this.querySelector('[data-selected-quantity]');
          if (input) input.value = this.quantity;
          this.updateSelectedPrices();
          return;
        }
        const addButton = event.target.closest('[data-add-selected]');
        if (addButton && this.selectedItem) this.addToCart(this.selectedItem, this.quantity, addButton);
      });

      this.querySelector('[data-result]')?.addEventListener('change', (event) => {
        const input = event.target.closest('[data-selected-quantity]');
        if (!input) return;
        this.quantity = this.clamp(Math.round(this.readNumber(input.value, 1)), 1, 999);
        this.renderResult();
      });

      this.querySelector('[data-result]')?.addEventListener('input', (event) => {
        const input = event.target.closest('[data-selected-quantity]');
        if (!input || !this.selectedItem) return;
        this.quantity = this.clamp(Math.round(this.readNumber(input.value, 1)), 1, 999);
        this.updateSelectedPrices();
      });

      this.querySelector('[data-directory]')?.addEventListener('click', (event) => {
        const row = event.target.closest('[data-directory-row]');
        if (!row) return;
        const item = this.items.find((candidate) => String(candidate.variantId) === String(row.dataset.directoryVariant));
        const input = row.querySelector('[data-directory-quantity]');
        if (!item || !input) return;

        const tierButton = event.target.closest('[data-directory-tier]');
        if (tierButton) {
          input.value = String(this.clamp(Math.round(this.readNumber(tierButton.dataset.directoryTier, 1)), 1, 999));
          this.updateDirectoryRow(row, item);
          return;
        }

        const stepButton = event.target.closest('[data-directory-step]');
        if (stepButton) {
          const quantity = this.clamp(Math.round(this.readNumber(input.value, 1)) + this.readNumber(stepButton.dataset.directoryStep, 0), 1, 999);
          input.value = String(quantity);
          this.updateDirectoryRow(row, item);
          return;
        }

        const addButton = event.target.closest('[data-directory-add]');
        if (addButton) {
          const quantity = this.clamp(Math.round(this.readNumber(input.value, 1)), 1, 999);
          input.value = String(quantity);
          this.updateDirectoryRow(row, item);
          this.addToCart(item, quantity, addButton);
        }
      });

      this.querySelector('[data-directory]')?.addEventListener('input', (event) => {
        const input = event.target.closest('[data-directory-quantity]');
        const row = input?.closest('[data-directory-row]');
        const item = row && this.items.find((candidate) => String(candidate.variantId) === String(row.dataset.directoryVariant));
        if (row && item) this.updateDirectoryRow(row, item);
      });

      this.querySelector('[data-directory]')?.addEventListener('change', (event) => {
        const input = event.target.closest('[data-directory-quantity]');
        const row = input?.closest('[data-directory-row]');
        const item = row && this.items.find((candidate) => String(candidate.variantId) === String(row.dataset.directoryVariant));
        if (!input || !row || !item) return;
        input.value = String(this.clamp(Math.round(this.readNumber(input.value, 1)), 1, 999));
        this.updateDirectoryRow(row, item);
      });

      this.querySelector('[data-delivery-retry]')?.addEventListener('click', () => this.requestDeliveryEstimate(true));
      this.querySelector('[data-delivery-dock-close]')?.addEventListener('click', () => {
        this.deliveryDockDismissed = true;
        this.updateDeliveryDock();
      });
    }

    selectableDimensions() {
      const values = new Set(this.items.flatMap((item) => [item.shortSide, item.longSide]));
      if (values.size) {
        for (let value = 200; value <= 300; value += 10) values.add(value);
      }
      return [...values].sort((a, b) => a - b);
    }

    configureRanges() {
      this.querySelectorAll('[data-range]').forEach((range) => {
        range.max = String(Math.max(0, this.dimensions.length - 1));
        this.renderRangeTicks(range.dataset.range);
        this.updateRangeVisual(range.dataset.range);
      });
    }

    renderRangeTicks(dimension) {
      const target = this.querySelector(`[data-range-ticks="${dimension}"]`);
      if (!target || !this.dimensions.length) return;
      const desired = [15, 50, 100, 150, 195, 250, 300];
      const values = [...new Set(desired.map((value) => this.dimensions[this.nearestDimensionIndex(value)]))];
      target.innerHTML = values.map((value) => {
        const index = this.dimensions.indexOf(value);
        const percent = this.dimensions.length > 1 ? (index / (this.dimensions.length - 1)) * 100 : 50;
        return `<span style="--tick-left:${percent.toFixed(3)}%">${this.formatNumber(value)}</span>`;
      }).join('');
    }

    updateRangeVisual(dimension) {
      const range = this.querySelector(`[data-range="${dimension}"]`);
      const wrap = range?.closest('.fa-gallery__range-wrap');
      if (!range || !wrap) return;
      const min = this.readNumber(range.min, 0);
      const max = this.readNumber(range.max, 0);
      const value = this.readNumber(range.value, min);
      const percent = max > min ? ((value - min) / (max - min)) * 100 : 50;
      wrap.style.setProperty('--fa-gallery-swipe-left', `${this.clamp(percent, 7, 93)}%`);
      range.setAttribute('aria-valuetext', `${this.formatNumber(this[dimension])} cm`);
    }

    readInitialFormat() {
      const parsed = this.parseFormat(new URL(window.location.href).searchParams.get('format') || '');
      if (parsed) {
        this.width = parsed[0];
        this.height = parsed[1];
      } else if (!this.byFormat.has(this.formatKey(50, 70)) && this.items[0]) {
        this.width = this.items[0].shortSide;
        this.height = this.items[0].longSide;
      }
      this.syncInputs();
    }

    syncInputs() {
      ['width', 'height'].forEach((dimension) => {
        const input = this.querySelector(`[data-number="${dimension}"]`);
        if (input) input.value = this[dimension];
        this.syncRange(dimension);
      });
      const direct = this.querySelector('[data-direct-input]');
      if (direct) direct.value = `${this.formatNumber(this.width)} × ${this.formatNumber(this.height)}`;
    }

    syncRange(dimension) {
      const range = this.querySelector(`[data-range="${dimension}"]`);
      if (range) {
        range.value = String(this.nearestDimensionIndex(this[dimension]));
        this.updateRangeVisual(dimension);
      }
    }

    nearestDimensionIndex(value) {
      if (!this.dimensions.length) return 0;
      return this.dimensions.reduce((bestIndex, candidate, index) => (
        Math.abs(candidate - value) < Math.abs(this.dimensions[bestIndex] - value) ? index : bestIndex
      ), 0);
    }

    updateSelection(options = {}) {
      const { updateUrl = true } = options;
      this.selectedItem = this.byFormat.get(this.formatKey(this.width, this.height)) || null;
      this.quantity = 1;
      this.renderResult();
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('format', `${this.formatNumber(this.width)}x${this.formatNumber(this.height)}`);
        window.history.replaceState({}, '', url);
      }
    }

    async loadSupportMap() {
      if (!this.config.supportMapUrl) return;
      try {
        const url = new URL(this.config.supportMapUrl, window.location.origin);
        url.searchParams.set('locale', this.locale);
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok || !payload.overrides || typeof payload.overrides !== 'object') return;
        this.supportOverrides = payload.overrides;
        this.renderDirectory();
      } catch {
        // Standard rules keep every back preview accurate when the override endpoint is unavailable.
      }
    }

    bindDeliveryDock() {
      this.deliveryDockUpdate = () => {
        window.cancelAnimationFrame(this.deliveryDockFrame);
        this.deliveryDockFrame = window.requestAnimationFrame(() => this.updateDeliveryDock());
      };
      window.addEventListener('scroll', this.deliveryDockUpdate, { passive: true });
      window.addEventListener('resize', this.deliveryDockUpdate, { passive: true });
      this.updateDeliveryDock();
    }

    updateDeliveryDock() {
      const delivery = this.querySelector('[data-delivery]');
      const dock = this.querySelector('[data-delivery-dock]');
      if (!delivery || !dock) return;
      const passedDelivery = delivery.getBoundingClientRect().bottom < 84;
      dock.classList.toggle('is-visible', passedDelivery && !this.deliveryDockDismissed);
    }

    renderResult() {
      const target = this.querySelector('[data-result]');
      if (!target) return;
      if (!this.validDimension(this.width) || !this.validDimension(this.height)) {
        this.renderInvalid();
        return;
      }

      const format = `${this.formatNumber(this.width)} × ${this.formatNumber(this.height)} cm`;
      if (!this.selectedItem) {
        const canMakeCustom = Math.min(this.width, this.height) >= 15
          && Math.min(this.width, this.height) <= 195 && Math.max(this.width, this.height) <= 300;
        const params = new URLSearchParams({
          kc_type: 'custom_4cm',
          kc_length: this.formatNumber(this.width),
          kc_width: this.formatNumber(this.height),
          kc_custom: '1',
        });
        target.innerHTML = `
          <div class="fa-gallery__custom">
            ${this.renderProportionalFrame(this.width, this.height)}
            <div>
              <p class="fa-gallery__result-kicker">${this.escapeHtml(this.t('selectedPrefix'))}: ${this.escapeHtml(format)}</p>
              <h3>${this.escapeHtml(this.t(canMakeCustom ? 'customHeading' : 'contactHeading'))}</h3>
              <p>${this.escapeHtml(this.t(canMakeCustom ? 'customText' : 'contactText'))}</p>
              <a class="fa-gallery__primary-button" href="${this.escapeHtml(`${this.config.customUrl}?${params.toString()}${canMakeCustom ? '' : '&kc_request=1'}#MainContent`)}">${this.escapeHtml(this.t(canMakeCustom ? 'customCta' : 'contactCta'))}</a>
            </div>
          </div>`;
        return;
      }

      const item = this.selectedItem;
      const rule = item.tierRules[0];
      const tier = this.applicableTier(item, this.quantity);
      const unitPrice = tier?.price || item.price;
      const tiers = rule ? rule.tiers.map((entry, index) => `
        <button type="button" class="${tier?.minQuantity === entry.minQuantity ? 'is-active' : ''}" data-tier-quantity="${entry.minQuantity}" aria-pressed="${tier?.minQuantity === entry.minQuantity ? 'true' : 'false'}">
          <span>${this.escapeHtml(this.tierQuantityLabel(rule.tiers, index))}</span>
          <strong>${this.formatMoney(entry.price)}</strong>
        </button>`).join('') : '';
      const image = item.image ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.imageAlt || item.title)}" width="900" height="900" decoding="async">` : this.renderProportionalFrame(this.width, this.height);

      target.innerHTML = `
        <div class="fa-gallery__result-card">
          <div class="fa-gallery__result-media">${image}</div>
          <div class="fa-gallery__result-buy">
            <p class="fa-gallery__result-kicker">${this.escapeHtml(this.t('selectedPrefix'))}: ${this.escapeHtml(format)}</p>
            <h3>${this.escapeHtml(item.title)}</h3>
            <p class="fa-gallery__match">✓ ${this.escapeHtml(this.t('exactMatch'))}</p>
            ${tiers ? `<div class="fa-gallery__tiers">${tiers}</div>` : ''}
            <div class="fa-gallery__purchase-row">
              <div class="fa-gallery__quantity">
                <span>${this.escapeHtml(this.t('quantity'))}</span>
                <span class="fa-gallery__quantity-stepper">
                  <button type="button" data-selected-step="-1" aria-label="${this.escapeHtml(this.t('decreaseQuantity'))}">−</button>
                  <input type="number" min="1" max="999" step="1" inputmode="numeric" value="${this.quantity}" data-selected-quantity aria-label="${this.escapeHtml(this.t('quantity'))}">
                  <button type="button" data-selected-step="1" aria-label="${this.escapeHtml(this.t('increaseQuantity'))}">+</button>
                </span>
              </div>
              <dl class="fa-gallery__price">
                <div><dt>${this.escapeHtml(this.t('unitPrice'))}</dt><dd data-selected-unit>${this.formatMoney(unitPrice)}</dd></div>
                <div><dt>${this.escapeHtml(this.t('total'))}</dt><dd><strong data-selected-total>${this.formatMoney(unitPrice * this.quantity)}</strong></dd></div>
              </dl>
            </div>
            <div class="fa-gallery__selected-delivery" aria-live="polite">
              <span>${this.escapeHtml(this.t('deliveryLabel'))}</span>
              <strong data-delivery-text>${this.escapeHtml(this.deliveryText || this.t(this.deliveryError ? 'deliveryError' : 'deliveryLoading'))}</strong>
              <button type="button" data-selected-delivery-retry${this.deliveryError ? '' : ' hidden'}>${this.escapeHtml(this.t('deliveryRetry'))}</button>
            </div>
            <div class="fa-gallery__actions">
              <button type="button" class="fa-gallery__primary-button" data-add-selected>${this.escapeHtml(this.t('add'))}</button>
              <a href="${this.escapeHtml(item.url)}">${this.escapeHtml(this.t('details'))} →</a>
            </div>
            <p class="fa-gallery__cart-status" data-cart-status role="status"></p>
          </div>
        </div>`;
    }

    renderInvalid() {
      const target = this.querySelector('[data-result]');
      if (target) target.innerHTML = `<p class="fa-gallery__error" role="alert">${this.escapeHtml(this.t('invalidFormat'))}</p>`;
    }

    updateSelectedPrices() {
      const tier = this.applicableTier(this.selectedItem, this.quantity);
      const unitPrice = tier?.price || this.selectedItem.price;
      this.querySelectorAll('[data-tier-quantity]').forEach((button) => {
        const active = this.readNumber(button.dataset.tierQuantity, 0) === tier?.minQuantity;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      const unit = this.querySelector('[data-selected-unit]');
      const total = this.querySelector('[data-selected-total]');
      if (unit) unit.textContent = this.formatMoney(unitPrice);
      if (total) total.textContent = this.formatMoney(unitPrice * this.quantity);
    }

    renderProportionalFrame(width, height) {
      const ratio = this.clamp(width / height, 0.18, 5.5);
      const landscape = ratio >= 1;
      const displayWidth = landscape ? 230 : Math.max(54, 230 * ratio);
      const displayHeight = landscape ? Math.max(54, 230 / ratio) : 230;
      return `<div class="fa-gallery__proportion" style="--preview-width:${displayWidth}px;--preview-height:${displayHeight}px" aria-hidden="true"><span></span><b>4 cm</b></div>`;
    }

    renderDirectory() {
      const target = this.querySelector('[data-directory]');
      if (!target || !this.items.length) return;
      const groups = new Map();
      this.items.forEach((item) => {
        const key = this.formatNumber(item.shortSide);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      });
      const orderedGroups = [...groups.entries()].sort(([a], [b]) => Number(a) - Number(b));
      const firstShortSide = orderedGroups[0]?.[0];

      target.innerHTML = orderedGroups.map(([shortSide, items]) => `
        <details class="fa-gallery__format-group" ${shortSide === firstShortSide ? 'open' : ''}>
          <summary><span>${this.escapeHtml(this.t('directoryGroup'))} ${this.escapeHtml(shortSide)} cm</span></summary>
          <div class="fa-gallery__format-rows">
            <div class="fa-gallery__discount-hint">
              <strong>${this.escapeHtml(this.t('quantityDiscount'))}</strong>
              <span>${this.escapeHtml(this.t('quantityDiscountHint'))}</span>
            </div>
            ${items.map((item) => this.renderDirectoryRow(item)).join('')}
          </div>
        </details>`).join('');
    }

    renderDirectoryRow(item) {
      const tiers = item.tierRules[0]?.tiers || [];
      const displayTiers = tiers.length ? tiers : [{ minQuantity: 1, price: item.price }];
      const initialTier = this.applicableTier(item, 1);
      const initialUnitPrice = initialTier?.price || item.price;
      const tierButtons = displayTiers.map((tier, index) => `
        <button type="button" class="fa-gallery__row-tier${index === 0 ? ' is-active' : ''}" data-directory-tier="${tier.minQuantity}" aria-pressed="${index === 0 ? 'true' : 'false'}">
          <span>${this.escapeHtml(this.tierQuantityLabel(displayTiers, index))}</span>
          <strong>${this.formatMoney(tier.price)}</strong>
        </button>`).join('');
      return `
        <div class="fa-gallery__format-row" data-directory-row data-directory-variant="${this.escapeHtml(item.variantId)}">
          <div class="fa-gallery__format-name-block">
            <a href="${this.escapeHtml(item.url)}" class="fa-gallery__format-name">${this.formatNumber(item.shortSide)} × ${this.formatNumber(item.longSide)} cm</a>
            ${this.renderDirectoryBackPreview(item)}
          </div>
          <div class="fa-gallery__row-tiers">${tierButtons}</div>
          <div class="fa-gallery__row-quantity">
            <span>${this.escapeHtml(this.t('quantity'))}</span>
            <div class="fa-gallery__row-stepper">
              <button type="button" data-directory-step="-1" aria-label="${this.escapeHtml(this.t('decreaseQuantity'))}">−</button>
              <input type="number" min="1" max="999" step="1" value="1" inputmode="numeric" data-directory-quantity aria-label="${this.escapeHtml(this.t('quantity'))}">
              <button type="button" data-directory-step="1" aria-label="${this.escapeHtml(this.t('increaseQuantity'))}">+</button>
            </div>
          </div>
          <dl class="fa-gallery__row-total">
            <div><dt>${this.escapeHtml(this.t('unitPrice'))}</dt><dd data-directory-unit>${this.formatMoney(initialUnitPrice)}</dd></div>
            <div><dt>${this.escapeHtml(this.t('total'))}</dt><dd><strong data-directory-total>${this.formatMoney(initialUnitPrice)}</strong></dd></div>
          </dl>
          <button type="button" class="fa-gallery__row-add" data-directory-add="${this.escapeHtml(item.variantId)}" aria-label="${this.escapeHtml(this.t('add'))}: ${this.formatNumber(item.shortSide)} × ${this.formatNumber(item.longSide)} cm">${this.escapeHtml(this.t('add'))}</button>
          <p class="fa-gallery__row-cart-status" data-row-cart-status role="status" aria-live="polite"></p>
        </div>`;
    }

    renderDirectoryBackPreview(item) {
      const longSide = Math.max(item.shortSide, item.longSide);
      const shortSide = Math.min(item.shortSide, item.longSide);
      const previewWidth = 112;
      const previewHeight = Math.max(26, Math.min(72, (shortSide / longSide) * previewWidth));
      const supportType = this.supportTypeFor(item.shortSide, item.longSide, 4);
      const supportLabel = this.supportLabel(supportType);
      const querleisteClass = previewWidth >= previewHeight
        ? 'fa-gallery__back-strut fa-gallery__back-strut--vertical'
        : 'fa-gallery__back-strut fa-gallery__back-strut--horizontal';
      const struts = {
        none: '',
        querleiste: `<span class="${querleisteClass}"></span>`,
        kreuz: '<span class="fa-gallery__back-strut fa-gallery__back-strut--horizontal"></span><span class="fa-gallery__back-strut fa-gallery__back-strut--vertical"></span>',
        doppelkreuz: '<span class="fa-gallery__back-strut fa-gallery__back-strut--horizontal"></span><span class="fa-gallery__back-strut fa-gallery__back-strut--vertical fa-gallery__back-strut--left"></span><span class="fa-gallery__back-strut fa-gallery__back-strut--vertical fa-gallery__back-strut--right"></span>',
      }[supportType] || '';

      return `
        <div class="fa-gallery__row-support" aria-label="${this.escapeHtml(this.t('supportBack'))}: ${this.escapeHtml(supportLabel)}">
          <span class="fa-gallery__back-frame fa-gallery__back-frame--${this.escapeHtml(supportType)}" style="--back-width:${previewWidth}px;--back-height:${previewHeight.toFixed(2)}px">
            <span class="fa-gallery__back-canvas"></span>
            ${struts}
          </span>
          <small>${this.escapeHtml(supportLabel)}</small>
        </div>`;
    }

    supportTypeFor(length, width, thicknessCm) {
      const override = this.supportOverrides?.[this.supportKey(thicknessCm, length, width)];
      if (['none', 'querleiste', 'kreuz', 'doppelkreuz'].includes(override)) return override;
      const longSide = Math.max(length, width);
      const shortSide = Math.min(length, width);
      const sidesAtLeast50 = (length >= 50 ? 1 : 0) + (width >= 50 ? 1 : 0);
      if (longSide >= 220) return 'doppelkreuz';
      if (sidesAtLeast50 >= 2) return 'kreuz';
      if (sidesAtLeast50 === 1 || (longSide >= 120 && shortSide >= 20 && shortSide <= 40)) return 'querleiste';
      return 'none';
    }

    supportKey(thicknessCm, length, width) {
      return `${thicknessCm}:${Math.min(length, width)}x${Math.max(length, width)}`;
    }

    supportLabel(type) {
      return {
        none: this.t('supportNone'),
        querleiste: this.t('supportBar'),
        kreuz: this.t('supportCross'),
        doppelkreuz: this.t('supportDoubleCross'),
      }[type] || this.t('supportBack');
    }

    updateDirectoryRow(row, item) {
      const input = row.querySelector('[data-directory-quantity]');
      const quantity = this.clamp(Math.round(this.readNumber(input?.value, 1)), 1, 999);
      const tier = this.applicableTier(item, quantity);
      const unitPrice = tier?.price || item.price;
      row.querySelectorAll('[data-directory-tier]').forEach((button) => {
        const active = this.readNumber(button.dataset.directoryTier, 0) === (tier?.minQuantity || 1);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      const unit = row.querySelector('[data-directory-unit]');
      const total = row.querySelector('[data-directory-total]');
      if (unit) unit.textContent = this.formatMoney(unitPrice);
      if (total) total.textContent = this.formatMoney(unitPrice * quantity);
    }

    tierQuantityLabel(tiers, index) {
      const tier = tiers[index];
      if (!tier) return '';
      const prefix = index === tiers.length - 1 && tier.minQuantity > 1 ? `${this.t('from')} ` : '';
      return `${prefix}${tier.minQuantity} ${this.t('pieceShort')}`;
    }

    applicableTier(item, quantity) {
      const tiers = item.tierRules[0]?.tiers || [];
      return tiers.reduce((selected, tier) => quantity >= tier.minQuantity ? tier : selected, null);
    }

    async addToCart(item, quantity, button) {
      const isDirectory = button.hasAttribute('data-directory-add');
      const row = isDirectory ? button.closest('[data-directory-row]') : null;
      const oldLabel = button.textContent;
      const status = isDirectory ? row?.querySelector('[data-row-cart-status]') : this.querySelector('[data-cart-status]');
      button.classList.remove('is-success', 'is-error');
      button.classList.add('is-loading');
      button.disabled = true;
      button.textContent = this.t('adding');
      if (status) status.textContent = '';
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(item.variantId), quantity }),
        });
        if (!response.ok) throw new Error(this.t('cartError'));
        const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((result) => result.json()).catch(() => null);
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: this.closest('[data-section-id]')?.dataset.sectionId, data: { source: 'galerie-collection', itemCount: cart?.item_count } },
        }));
        document.querySelector('cart-drawer-component')?.open?.();
        const successLabel = isDirectory ? `${quantity} ${this.t('pieceShort')} ${this.t('inCart')}` : this.t('added');
        if (status) status.textContent = isDirectory ? `✓ ${successLabel}` : this.t('cartSuccess');
        button.classList.remove('is-loading');
        button.classList.add('is-success');
        button.textContent = `✓ ${successLabel}`;
        window.setTimeout(() => {
          button.disabled = false;
          button.classList.remove('is-success');
          button.textContent = oldLabel;
        }, 3600);
      } catch (error) {
        if (status) status.textContent = error.message || this.t('cartError');
        button.classList.remove('is-loading');
        button.classList.add('is-error');
        button.disabled = false;
        button.textContent = oldLabel;
      }
    }

    async requestDeliveryEstimate(force = false) {
      if (this.deliveryRequested && !force) return;
      this.deliveryRequested = true;
      this.deliveryController?.abort();
      const controller = new AbortController();
      this.deliveryController = controller;
      this.deliveryText = '';
      this.deliveryError = false;
      this.updateDeliveryText();
      const timeout = window.setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(this.config.deliveryUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({ thicknessCm: 4, productGroup: 'gallery_4cm', locale: this.locale }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok || !payload.estimate?.deliveryText) throw new Error('delivery');
        if (this.deliveryController !== controller) return;
        this.deliveryText = String(payload.estimate.deliveryText);
      } catch {
        if (this.deliveryController !== controller) return;
        this.deliveryError = true;
      } finally {
        window.clearTimeout(timeout);
        if (this.deliveryController === controller) {
          this.deliveryController = null;
          this.updateDeliveryText();
        }
      }
    }

    updateDeliveryText() {
      const status = this.deliveryText || this.t(this.deliveryError ? 'deliveryError' : 'deliveryLoading');
      this.querySelectorAll('[data-delivery-text], [data-delivery-dock-text]').forEach((target) => {
        target.textContent = status;
      });
      this.querySelectorAll('[data-delivery-retry], [data-selected-delivery-retry]').forEach((button) => {
        button.hidden = !this.deliveryError;
      });
    }

    parseFormat(value) {
      const match = String(value).trim().match(/(\d+(?:[.,]\d+)?)\s*(?:x|×|\*|mal|by)\s*(\d+(?:[.,]\d+)?)/i);
      if (!match) return null;
      const width = this.readNumber(match[1].replace(',', '.'), 0);
      const height = this.readNumber(match[2].replace(',', '.'), 0);
      return this.validDimension(width) && this.validDimension(height) ? [width, height] : null;
    }

    formatKey(a, b) {
      return [this.readNumber(a, 0), this.readNumber(b, 0)].sort((left, right) => left - right).map((value) => this.formatNumber(value)).join('x');
    }

    validDimension(value) {
      return Number.isFinite(value) && value > 0 && value <= 999;
    }

    formatNumber(value) {
      return Number.isInteger(Number(value)) ? String(Number(value)) : String(Number(value).toFixed(1)).replace('.', ',');
    }

    formatMoney(cents) {
      const amount = this.readNumber(cents, 0) / 100;
      try {
        return new Intl.NumberFormat(this.locale, { style: 'currency', currency: String(this.config.currency || 'CHF'), minimumFractionDigits: 2 }).format(amount);
      } catch {
        return `CHF ${amount.toFixed(2)}`;
      }
    }

    readNumber(value, fallback) {
      const number = Number.parseFloat(value);
      return Number.isFinite(number) ? number : fallback;
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    t(key) {
      return this.i18n[key] || key;
    }

    escapeHtml(value) {
      return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  }

  if (!customElements.get(ELEMENT_NAME)) customElements.define(ELEMENT_NAME, GalerieCollection);
})();
